import { Checkbox, Icon, spacing, Text } from '@scality/core-ui';
import { useSelect } from 'downshift';
import type React from 'react';
import { useId, useMemo } from 'react';
import styled from 'styled-components';
import {
  mergeRefs,
  SelectField,
  SelectFieldLabel,
  SelectFieldText,
  SelectFieldTrigger,
  SelectMenuPanel,
  SelectMenuPlainList,
  SelectMenuRow,
  SelectMenuScroll,
  SelectRowCheckbox,
  SelectWrapper,
  selectionBudget,
  useFloatingSelectMenu,
} from '../../../components/select';
import { blankSelectionOf, needsPick, selectionCapOf } from '../../filters';
import type { ControlOption } from '../../types/controls';
import type { ControlComponentProps } from './types';

/* Explicit widths, not `100%`: the panel is positioned `fixed`, so a percentage would
   resolve against the viewport instead of the trigger. */
const Menu = styled(SelectMenuPanel)`
  min-width: 11rem;
  max-width: min(24rem, calc(100vw - 2rem));
  max-height: 14rem;
`;

const Budget = styled(Text)`
  display: block;
  flex-shrink: 0;
  padding: ${spacing.r8} ${spacing.r8} 0;
`;

/**
 * A single- or multi-select dropdown backed by dynamically-resolved options. Selection is
 * owned by the parent (`value`); for multi-select the menu stays open and clicks toggle
 * membership — each row led by a checkbox, as the portal-backed pickers do — while for
 * single-select a click replaces and closes.
 *
 * A multi-select holds at most `control.maxSelections`, each pick being another series on
 * every chart. At the cap unpicked rows stop responding while picked ones stay live, so there
 * is always a way back under it. Left blank, a capped control shows every option picked when
 * they fit under the cap, and asks for a pick when they do not.
 */
export const DropdownControl: React.FC<ControlComponentProps> = ({
  control,
  options,
  value,
  onChange,
  loading,
  disabled,
  defaultValue,
}) => {
  const multiple = control.multiple ?? false;
  const maxSelections = selectionCapOf(control);
  const selected = value.length > 0 ? value : blankSelectionOf(control, options);
  const mustPick = needsPick(control, options, value);
  const isAtCap = maxSelections !== undefined && selected.length >= maxSelections;
  const canToggle = (option: ControlOption) => !isAtCap || selected.includes(option.value);
  // Unique id linking the visible label to the trigger button (aria-labelledby via downshift).
  const labelId = useId();
  // Keep the trigger label stable across option (re)fetches — the current selection always
  // shows; a fetch only affects the menu contents, never the toolbar layout.
  const label = useMemo(() => {
    // "Default" is not a placeholder: the render applies a value of its own, so the dashboard is
    // scoped — just not by a choice the user made.
    if (selected.length === 0) {
      if (loading) return 'Loading…';
      if (mustPick) return `Select up to ${maxSelections}`;
      if (defaultValue?.length) return 'Default';
      if (options.length === 0) return `No ${control.label.toLowerCase()} available`;
      return 'Select…';
    }
    if (multiple && options.length > 1 && selected.length === options.length) return 'All';
    if (selected.length === 1) {
      return options.find((option) => option.value === selected[0])?.label ?? selected[0];
    }
    return `${selected.length} selected`;
  }, [selected, mustPick, maxSelections, options, loading, defaultValue, control.label, multiple]);

  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps, highlightedIndex } = useSelect({
    labelId,
    items: options,
    itemToString: (option) => option?.label ?? '',
    selectedItem: null,
    stateReducer: (state, { type, changes }) => {
      // Keep the menu open across multi-select toggles so several values can be picked.
      const keepOpen =
        multiple &&
        (type === useSelect.stateChangeTypes.ToggleButtonKeyDownEnter ||
          type === useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton ||
          type === useSelect.stateChangeTypes.ItemClick);
      return keepOpen ? { ...changes, isOpen: true, highlightedIndex: state.highlightedIndex } : changes;
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) return;
      const picked = selectedItem.value;
      if (multiple) {
        // Refused here rather than by the dimming alone, so a keyboard Enter is held to the cap too.
        if (!canToggle(selectedItem)) return;
        onChange(selected.includes(picked) ? selected.filter((v) => v !== picked) : [...selected, picked]);
      } else {
        onChange([picked]);
      }
    },
  });

  // The filter bar sits at the top of the dashboard, so the menu opens below it. Floating UI
  // (fixed strategy) keeps it clear of the dashboard panel's own scrolling and clipping.
  const { refs: floatingRefs, floatingStyles } = useFloatingSelectMenu({
    isOpen,
    preferredBelow: true,
    mainAxisAbovePx: 8,
    mainAxisBelowPx: 8,
  });

  const { ref: menuRef, style: menuStyle, ...menuRest } = getMenuProps();
  // Only a genuinely empty menu is unusable, and not while it is still loading.
  const nothingToChoose = !loading && options.length === 0;
  // One source for both downshift and the DOM, or the button ignores clicks while looking live.
  const isDisabled = disabled || nothingToChoose;
  const { ref: toggleRef, ...toggleRest } = getToggleButtonProps({ disabled: isDisabled });

  return (
    <SelectField>
      <SelectFieldLabel id={labelId} variant="Basic" color="textSecondary">
        {control.label}
      </SelectFieldLabel>
      <SelectWrapper>
        <SelectFieldTrigger
          type="button"
          ref={mergeRefs(floatingRefs.setReference, toggleRef)}
          {...toggleRest}
          disabled={isDisabled}
          $isOpen={isOpen}
        >
          <SelectFieldText>{label}</SelectFieldText>
          <Icon name="ArrowsUpDown" size="xs" />
        </SelectFieldTrigger>
        <Menu
          ref={mergeRefs(floatingRefs.setFloating, menuRef)}
          {...menuRest}
          style={{ ...menuStyle, ...floatingStyles }}
          aria-hidden={!isOpen}
          $isOpen={isOpen}
        >
          {isOpen && maxSelections !== undefined && (
            <Budget variant="Smaller" color="textSecondary">
              {selectionBudget(selected.length, maxSelections)}
            </Budget>
          )}
          <SelectMenuScroll>
            <SelectMenuPlainList>
              {isOpen && loading && options.length === 0 && (
                <SelectMenuRow $highlighted={false}>
                  <Text variant="Small" color="textSecondary">
                    Loading…
                  </Text>
                </SelectMenuRow>
              )}
              {isOpen &&
                options.map((option, index) => (
                  <SelectMenuRow
                    key={option.value}
                    $highlighted={highlightedIndex === index}
                    {...getItemProps({ item: option, index })}
                    $multi={multiple}
                    $disabled={!canToggle(option)}
                    aria-disabled={!canToggle(option)}
                  >
                    {multiple && (
                      <SelectRowCheckbox>
                        <Checkbox
                          checked={selected.includes(option.value)}
                          disabled={!canToggle(option)}
                          readOnly
                          tabIndex={-1}
                        />
                      </SelectRowCheckbox>
                    )}
                    <Text>{option.label}</Text>
                    {!multiple && value.includes(option.value) && <Icon name="Check" size="sm" />}
                  </SelectMenuRow>
                ))}
              {isOpen && !loading && options.length === 0 && (
                <SelectMenuRow $highlighted={false}>
                  <Text variant="Small" color="textSecondary">
                    No values available
                  </Text>
                </SelectMenuRow>
              )}
            </SelectMenuPlainList>
          </SelectMenuScroll>
        </Menu>
      </SelectWrapper>
    </SelectField>
  );
};
